import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { Visibility, VisibilityOff, AdminPanelSettings, Person } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';

const Register = ({ isAdmin = false }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: isAdmin ? 'admin' : 'candidate',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const { register } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    const userData = {
      email: formData.email,
      password: formData.password,
      role: formData.role,
      profile: {
        firstName: formData.firstName,
        lastName: formData.lastName,
      },
    };

    const result = await register(userData);
    
    if (result.success) {
      enqueueSnackbar('Registration successful!', { variant: 'success' });
      // Navigate based on user role
      if (result.user?.role === 'admin' || result.user?.role === 'recruiter') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.message || 'Registration failed. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: isAdmin ? '#1a237e' : '#2196f3',
                mb: 2,
                mx: 'auto'
              }}
            >
              {isAdmin ? (
                <AdminPanelSettings sx={{ color: 'white', fontSize: 30 }} />
              ) : (
                <Person sx={{ color: 'white', fontSize: 30 }} />
              )}
            </Box>
            <Typography component="h1" variant="h4" gutterBottom>
              {isAdmin ? 'Admin Registration' : 'Create Account'}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {isAdmin ? 'Create administrator account' : 'Join our hiring platform'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoComplete="given-name"
                  name="firstName"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  autoFocus
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              {!isAdmin && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="role-label">Role</InputLabel>
                    <Select
                      labelId="role-label"
                      id="role"
                      name="role"
                      value={formData.role}
                      label="Role"
                      onChange={handleChange}
                      disabled={loading}
                    >
                      <MenuItem value="candidate">Candidate</MenuItem>
                      <MenuItem value="recruiter">Recruiter</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: isAdmin ? 'linear-gradient(135deg, #1a237e, #3949ab)' : 'linear-gradient(135deg, #2196f3, #1976d2)',
                '&:hover': {
                  background: isAdmin ? 'linear-gradient(135deg, #0d47a1, #1a237e)' : 'linear-gradient(135deg, #1976d2, #1565c0)'
                }
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign Up'
              )}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                to={isAdmin ? '/admin-login' : '/login'}
                style={{
                  textDecoration: 'none',
                  color: isAdmin ? '#1a237e' : '#2196f3',
                  fontWeight: 'medium'
                }}
              >
                {isAdmin ? 'Already have an admin account? Sign in' : 'Already have an account? Sign in'}
              </Link>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                to={isAdmin ? '/register' : '/admin-register'}
                style={{
                  textDecoration: 'none',
                  color: isAdmin ? '#1a237e' : '#2196f3',
                  fontSize: '0.875rem'
                }}
              >
                {isAdmin ? 'Register as Candidate' : 'Register as Administrator'}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
